import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { describePermissions, type InvitationResult, type RoleSummary, type UserList, type UserSummary } from '@kontor/shared';
import { api, ApiError } from '../api';
import { InvitationLink } from '../components/InvitationLink';

/**
 * Benutzer einladen oder bearbeiten. Beim Einladen bekommt der Benutzer kein Passwort, sondern einen Link, über den er es
 * selbst festlegt. Beim Bearbeiten lassen sich Name und Rolle ändern, die E-Mail-Adresse bleibt, denn mit ihr meldet man sich an.
 */
export function UserFormPage() {
  const { id } = useParams();
  const isCreate = !id;
  const navigate = useNavigate();
  const client = useQueryClient();

  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserList>('GET', '/users') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: async () => (await api<{ data: RoleSummary[] }>('GET', '/users/roles')).data });
  const existing: UserSummary | undefined = id ? users.data?.data.find((u) => u.id === id) : undefined;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('');
  const [extraKeys, setExtraKeys] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationResult | null>(null);
  const [sendMail, setSendMail] = useState<boolean | null>(null);
  const [mailRequested, setMailRequested] = useState(false);
  const [renewMail, setRenewMail] = useState<boolean | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current || !existing) return;
    loaded.current = true;
    setName(existing.name);
    setEmail(existing.email);
    setRoleKey(existing.role.key);
    setExtraKeys(existing.extraRoles.map((r) => r.key));
  }, [existing]);

  // Vorgabe beim Einladen: die erste Rolle, die kein Inhaber ist
  const effectiveRole = roleKey || (isCreate ? (roles.data?.find((r) => r.key !== 'owner')?.key ?? '') : '');
  const selectedRole = roles.data?.find((r) => r.key === effectiveRole);
  // Zusätzliche Rollen: nicht die Hauptrolle und nie „Inhaber“. Mit mehreren Rollen gelten alle Rechte zusammen.
  const extraChoices = (roles.data ?? []).filter((r) => r.key !== 'owner' && r.key !== effectiveRole);
  const extraSelected = extraKeys.filter((k) => k !== effectiveRole);
  const extraRoles = extraChoices.filter((r) => extraSelected.includes(r.key));
  const mailEnabled = users.data?.meta.mailEnabled ?? false;
  // Vorgabe: Ist der Mailversand eingerichtet, geht die Einladung per E-Mail raus
  const wantsMail = mailEnabled && (sendMail ?? true);
  const wantsRenewMail = mailEnabled && (renewMail ?? true);

  const onError = (e: unknown) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (e instanceof ApiError) {
      setErrors(e.status === 422 ? e.fields : {});
      setFormError(e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message);
    } else {
      setFormError('Die Änderungen konnten nicht gespeichert werden.');
    }
  };

  const invite = useMutation({
    mutationFn: () => api<{ data: InvitationResult }>('POST', '/users', { name: name.trim(), email: email.trim(), roleKey: effectiveRole, extraRoleKeys: extraSelected, sendMail: wantsMail }),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['users'] });
      setMailRequested(wantsMail);
      setInvitation(res.data);
    },
    onError,
  });
  const renew = useMutation({
    mutationFn: () => api<{ data: InvitationResult }>('POST', `/users/${id}/invitation`, { sendMail: wantsRenewMail }),
    onSuccess: (res) => {
      client.invalidateQueries({ queryKey: ['users'] });
      setMailRequested(wantsRenewMail);
      setInvitation(res.data);
    },
    onError,
  });
  const save = useMutation({
    mutationFn: () => api<{ data: UserSummary }>('PATCH', `/users/${id}`, { name: name.trim(), roleKey: effectiveRole, extraRoleKeys: extraSelected }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['users'] });
      navigate('/einrichtung/benutzer', { replace: true });
    },
    onError,
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setErrors({});
    setFormError(null);
    (isCreate ? invite : save).mutate();
  }

  if (!isCreate && users.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!isCreate && !existing) {
    return (
      <div className="card narrow">
        <h1>Benutzer nicht gefunden</h1>
        <Link to="/einrichtung/benutzer">Zur Benutzerliste</Link>
      </div>
    );
  }

  if (invitation) {
    return (
      <>
        <InvitationLink result={invitation} mailRequested={mailRequested} />
        <p><Link className="btn btn--secondary" to="/einrichtung/benutzer">Zur Benutzerliste</Link></p>
      </>
    );
  }

  const pending = invite.isPending || save.isPending;
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <>
      <p><Link to="/einrichtung/benutzer" className="back-link">← Benutzer und Rollen</Link></p>
      <div className="page-head">
        <h1>{isCreate ? 'Benutzer einladen' : existing!.name}</h1>
      </div>
      {isCreate && <p className="muted">Die Person bekommt keinen Passwortvorschlag, sondern einen Link, über den sie ihr Passwort selbst festlegt.</p>}

      <form className="card" onSubmit={submit} noValidate>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <div className="form-grid">
          <label className="field">
            <span>Name <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" aria-invalid={!!errors.name} />
            {error('name')}
          </label>
          <label className="field">
            <span>E-Mail <abbr title="Pflichtfeld">*</abbr></span>
            <input className="input" type="email" value={email} disabled={!isCreate} onChange={(e) => setEmail(e.target.value)} autoComplete="off" aria-invalid={!!errors.email} />
            {error('email')}
            {!isCreate && <small className="muted">Mit dieser Adresse meldet sich die Person an. Sie lässt sich nicht ändern.</small>}
          </label>
          <label className="field">
            <span>Rolle <abbr title="Pflichtfeld">*</abbr></span>
            <select className="input" value={effectiveRole} onChange={(e) => setRoleKey(e.target.value)}>
              {roles.data?.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
            {error('roleKey')}
          </label>
        </div>

        {extraChoices.length > 0 && effectiveRole !== 'owner' && (
          <fieldset className="field">
            <legend>Weitere Rollen</legend>
            <small className="muted">Für Mitarbeiter mit mehreren Aufgaben, zum Beispiel Dispatcher und Fahrer. Die Rechte aller Rollen gelten zusammen.</small>
            <span className="checks">
              {extraChoices.map((r) => (
                <label key={r.key} className="check">
                  <input
                    type="checkbox"
                    checked={extraSelected.includes(r.key)}
                    onChange={(e) => setExtraKeys(e.target.checked ? [...extraSelected, r.key] : extraSelected.filter((k) => k !== r.key))}
                  />
                  <span>{r.name}</span>
                </label>
              ))}
            </span>
            {error('extraRoleKeys')}
          </fieldset>
        )}

        {selectedRole && (
          <div className="rights-box">
            <strong>{extraRoles.length > 0 ? `Rechte insgesamt (${[selectedRole.name, ...extraRoles.map((r) => r.name)].join(' + ')})` : `Rechte der Rolle „${selectedRole.name}“`}</strong>
            {describePermissions([...selectedRole.permissions, ...extraRoles.flatMap((r) => r.permissions)]).length === 0 ? (
              <p className="muted small">Keine Rechte in den Modulen, die Ihr Paket enthält.</p>
            ) : (
              <ul className="rights">
                {describePermissions([...selectedRole.permissions, ...extraRoles.flatMap((r) => r.permissions)]).map((r) => <li key={r.module}>{r.label}: {r.actions.join(', ')}</li>)}
              </ul>
            )}
          </div>
        )}

        {isCreate && (
          mailEnabled ? (
            <label className="check">
              <input type="checkbox" checked={wantsMail} onChange={(e) => setSendMail(e.target.checked)} />
              <span>Einladung zusätzlich per E-Mail an die angegebene Adresse schicken</span>
            </label>
          ) : (
            <p className="muted small">Der Mailversand ist nicht eingerichtet. Nach dem Erstellen geben Sie den Einladungslink persönlich weiter.</p>
          )
        )}

        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={pending || !effectiveRole}>
            {pending ? 'Wird gespeichert …' : isCreate ? 'Einladung erstellen' : 'Speichern'}
          </button>
          <Link className="btn btn--secondary" to="/einrichtung/benutzer">Abbrechen</Link>
        </div>
      </form>

      {existing?.status === 'invited' && (
        <section className="card">
          <h2>Einladung</h2>
          <p>
            {existing.invitationExpiresAt && new Date(existing.invitationExpiresAt).getTime() < Date.now()
              ? 'Der Einladungslink ist abgelaufen.'
              : `Der Einladungslink gilt bis ${new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(existing.invitationExpiresAt ?? Date.now()))}.`}{' '}
            {existing.name} hat das Passwort noch nicht festgelegt. Aus Sicherheitsgründen lässt sich ein vorhandener Link nicht noch einmal anzeigen. Ein neuer Link macht den
            bisherigen ungültig.
          </p>
          {mailEnabled ? (
            <label className="check">
              <input type="checkbox" checked={wantsRenewMail} onChange={(e) => setRenewMail(e.target.checked)} />
              <span>Den neuen Link zusätzlich per E-Mail an {existing.email} schicken</span>
            </label>
          ) : (
            <p className="muted small">Der Mailversand ist nicht eingerichtet. Den neuen Link geben Sie persönlich weiter.</p>
          )}
          <button type="button" className="btn btn--secondary" disabled={renew.isPending} onClick={() => { setFormError(null); renew.mutate(); }}>
            {renew.isPending ? 'Wird erzeugt …' : 'Neuen Einladungslink erzeugen'}
          </button>
        </section>
      )}
    </>
  );
}
